import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  TextField,
  MenuItem,
  FormControl,
  Select,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Button,
  InputLabel,
  Snackbar,
  Autocomplete,
  Chip,
  Paper,
} from '@mui/material'
import { uploadToS3 } from '../../utils/uploadToS3'
import { useMutation, useQuery } from '@apollo/client'
import { createPrompt, addRatioToPrompt } from '../../graphql/mutation'
import { getAllTags } from '../../graphql/queries'
import ImageUploadField from '../../components/imageUpload'
import VideoAutoConvert from '../../components/VideoAutoConvert'
import ToolRatioManager from '../../components/ToolRatioManager'
import { designType, inferanceModelEnum } from '../../utils/enum'

const outPutFormat = [
  { value: 'webp', name: 'webp' },
  { value: 'jpg', name: 'jpg' },
  { value: 'png', name: 'png' },
]

const gender = [
  { value: 'MALE', name: 'MALE' },
  { value: 'FEMALE', name: 'FEMALE' },
  { value: 'OTHER', name: 'OTHER' },
]

const aspectRatio = [
  { value: '1:1', name: '1:1' },
  { value: '16:9', name: '16:9' },
  { value: '21:9', name: '21:9' },
  { value: '3:2', name: '3:2' },
  { value: '2:3', name: '2:3' },
  { value: '4:5', name: '4:5' },
  { value: '5:4', name: '5:4' },
  { value: '3:4', name: '3:4' },
  { value: '4:3', name: '4:3' },
  { value: '9:16', name: '9:16' },
  { value: '9:21', name: '9:21' },
]

const toolType = [
  { value: 'IMG2VIDEO', name: 'IMG2VIDEO' },
  { value: 'IMG2VIDEO_WITH_TEXT', name: 'IMG2VIDEO_WITH_TEXT' },
  { value: 'PREDICT_WITH_MODEL', name: 'PREDICT_WITH_MODEL' },
  { value: 'TEXT2VIDEO', name: 'TEXT2VIDEO' },
  { value: 'TRAIN', name: 'TRAIN' },
  { value: 'VIDEO2VIDEO', name: 'VIDEO2VIDEO' },
  {value: 'IMG2IMG', name: 'IMG2IMG'},
]

export default function CreatePromptPage() {
  const navigate = useNavigate()

  // Helper function to check if current tool type needs prediction parameters
  const needsPredictionParams = (toolType) => {
    return toolType === 'PREDICT_WITH_MODEL'
  }

  // Helper function to check if current tool type needs inference model
  const needsInferenceModel = (toolType) => {
    return toolType === 'PREDICT_WITH_MODEL' || toolType === 'IMG2IMG'
  }

  const [prompt, setPrompt] = useState({
    prompt: '',
    banner: '',
    secondBanner: '',
    videoUrl: '',
    gifUrl: '',
    webmUrl: '',
    webpUrl: '',
    designType: 'SMALL',
    toolType: 'PREDICT_WITH_MODEL',
    isVisible: true,
    isPublic: false,
    rowNumber: 1,
    createdAt: '',
    updatedAt: '',
    gender: 'OTHER',
    aspectRatio: '',
    guidanceScale: '',
    loraScale: '',
    numInferenceSteps: '',
    outputFormat: '',
    outputQuality: '',
    numOfOutPuts: '',
    promptSvg: '',
    beforeImage: '',
    tagIds: [],
    highlighted: false,
    templateId: '',
    inferanceModelEnum: '',
    intputImageCount: 0,
    cost: 10,
    name: '',
    detailImage: '',
  })

  const [file, setFile] = useState(null)
  const [beforeImageFile, setBeforeImageFile] = useState(null)
  const [selectedTags, setSelectedTags] = useState([])
  const [promptSvgFile, setPromptSvgFile] = useState(null)
  const [videoFile, setVideoFile] = useState(null)
  const [gifFile, setGifFile] = useState(null)
  const [webmFile, setWebmFile] = useState(null)
  const [webpFile, setWebpFile] = useState(null)
  const [secondBannerFile, setSecondBannerFile] = useState(null)
  const [detailImageFile, setDetailImageFile] = useState(null)
  const [localRatios, setLocalRatios] = useState([])

  const [progress, setProgress] = useState(false)
  const [openImageModal, setOpenImageModal] = useState(false)
  const [modalImageSrc, setModalImageSrc] = useState('')

  const [snackBarOpen, setSnackBarOpen] = useState(false)
  const [snackBarMessage, setSnackBarMessge] = useState(null)
  const [snackBarSeverity, setSnackBarSeverity] = useState('success')

  const { data: tagsData } = useQuery(getAllTags)
  const tags = tagsData?.getAllTags || []

  const handleImageClick = src => {
    setModalImageSrc(src)
    setOpenImageModal(true)
  }

  const handleChange = e => {
    const { name, value } = e.target
    setPrompt(prev => ({ ...prev, [name]: value }))
  }

  const handleBannerFileSelected = e => {
    const selectedFile = e.target.files[0]
    setFile(selectedFile)
  }

  const handleBeforeImageSelected = e => {
    const selectedFile = e.target.files[0]
    setBeforeImageFile(selectedFile)
  }

  const handlePromptSvgSelected = e => {
    const selectedFile = e.target.files[0]
    setPromptSvgFile(selectedFile)
  }

  const handleVideoSelected = e => {
    const selectedFile = e.target.files[0]
    setVideoFile(selectedFile)
  }

  const handleGifSelected = e => {
    const selectedFile = e.target.files[0]
    setGifFile(selectedFile)
  }

  const handleWebmSelected = e => {
    const selectedFile = e.target.files[0]
    setWebmFile(selectedFile)
  }

  const handleWebpSelected = e => {
    const selectedFile = e.target.files[0]
    setWebpFile(selectedFile)
  }

  const handleSecondBannerSelected = e => {
    const selectedFile = e.target.files[0]
    setSecondBannerFile(selectedFile)
  }

  const handleDetailImageSelected = e => {
    const selectedFile = e.target.files[0]
    setDetailImageFile(selectedFile)
  }

  const [createPromptMutation] = useMutation(createPrompt)
  const [addRatioMutation] = useMutation(addRatioToPrompt)

  const handleCreatePrompt = async () => {
    // Check required fields based on tool type
    const needsPredict = needsPredictionParams(prompt.toolType)
    const requiredFields = ['prompt']
    
    // Add tool-type specific required fields
    if (needsPredict) {
      requiredFields.push('outputFormat', 'aspectRatio')
    }
    
    setProgress(true)
    try {
      const s3Thumbnailfile = file ? await uploadToS3(file, 'img') : ''
      const s3SecondBannerFile = secondBannerFile ? await uploadToS3(secondBannerFile, 'img') : ''
      const beforeImagefile = beforeImageFile ? await uploadToS3(beforeImageFile, 'img') : ''
      const promptSvgfile = promptSvgFile ? await uploadToS3(promptSvgFile, 'img') : ''
      const s3VideoFile = videoFile ? await uploadToS3(videoFile, 'video') : ''
      const s3GifFile = gifFile ? await uploadToS3(gifFile, 'gif') : ''
      const s3WebmFile = webmFile ? await uploadToS3(webmFile, 'video') : ''
      const s3WebpFile = webpFile ? await uploadToS3(webpFile, 'img') : ''
      const s3DetailImageFile = detailImageFile ? await uploadToS3(detailImageFile, 'img') : ''

      const variables = {
        prompt: prompt.prompt,
        banner: s3Thumbnailfile,
        secondBanner: s3SecondBannerFile,
        videoUrl: s3VideoFile,
        gifUrl: s3GifFile,
        webmUrl: s3WebmFile,
        webpUrl: s3WebpFile,
        designType: prompt.designType,
        toolType: prompt.toolType,
        isVisible: prompt.isVisible,
        isPublic: prompt.isPublic,
        rowNumber: Number(prompt.rowNumber),
        gender: prompt.gender,
        aspectRatio: prompt.aspectRatio,
        guidanceScale: Number(prompt.guidanceScale),
        loraScale: Number(prompt.loraScale),
        numInferenceSteps: Number(prompt.numInferenceSteps),
        outputFormat: prompt.outputFormat,
        outputQuality: Number(prompt.outputQuality),
        numOfOutPuts: Number(prompt.numOfOutPuts),
        promptSvg: promptSvgfile,
        beforeImage: beforeImagefile,
        tagIds: prompt.tagIds,
        highlighted: prompt.highlighted,
        templateId: prompt.templateId,
        inferanceModelEnum: prompt.inferanceModelEnum || null,
        intputImageCount: Number(prompt.intputImageCount) || 0,
        cost: Number(prompt.cost) || 10,
        name: prompt.name,
        detailImage: s3DetailImageFile,
      }

      const { data } = await createPromptMutation({ 
        variables,
        // Refetch queries to update the table data
        refetchQueries: [
          'GetAllPromptsAdmin', // This will refresh the prompts table
        ],
        awaitRefetchQueries: true // Wait for refetch to complete
      })

      if (data && data.createPrompt) {
        const createdPromptId = data.createPrompt.id;
        
        // Add ratios if any were defined
        if (localRatios.length > 0) {
          try {
            for (const ratio of localRatios) {
              await addRatioMutation({
                variables: {
                  promptId: createdPromptId,
                  width: ratio.width,
                  height: ratio.height,
                  name: ratio.name,
                  aspectRatioX: ratio.aspectRatioX,
                  aspectRatioY: ratio.aspectRatioY,
                },
              });
            }
            setSnackBarMessge(`Prompt created successfully with ${localRatios.length} ratios`)
          } catch (ratioError) {
            console.warn('Error adding ratios:', ratioError);
            setSnackBarMessge('Prompt created successfully, but some ratios failed to save')
          }
        } else {
          setSnackBarMessge('Prompt created successfully')
        }
        
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        setTimeout(() => {
          // Navigate back to prompts with scroll restoration
          navigate('/prompts', { 
            state: { 
              scrollToPosition: true,
              preserveScroll: true 
            }
          });
        }, 1000)
      } else {
        throw new Error('No data returned')
      }
    } catch (error) {
      console.error('Error creating prompt:', error)
      setSnackBarMessge(`Create failed: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleSnackBarClose = () => {
    setSnackBarOpen(false)
  }

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={3000}
        onClose={handleSnackBarClose}
        message={snackBarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackBarClose} severity={snackBarSeverity} sx={{ width: '100%' }}>
          {snackBarMessage}
        </Alert>
      </Snackbar>

      {/* Sticky Header with Actions */}
      <Box 
        sx={{ 
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: 'white',
          borderBottom: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 4, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ color: '#212529', fontWeight: 'bold' }}>
            Create Prompt
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreatePrompt}
            sx={{ 
              borderRadius: 2,
              px: 4,
              backgroundColor: '#3498db',
              '&:hover': { backgroundColor: '#2980b9' }
            }}
          >
            Save Prompt
          </Button>
        </Box>
      </Box>
      {progress && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.7)',
            zIndex: 1300,
          }}
        >
          <CircularProgress size={80} thickness={5} />
        </Box>
      )}

      {/* Main Content Container */}
      <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
        
        {/* Basic Information Card */}
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          <Typography variant='h5' sx={{ mb: 4, fontWeight: 'bold', color: '#2c3e50' }}>
            Basic Information
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                name="prompt"
                variant="outlined"
                label="Prompt"
                multiline
                rows={6}
                value={prompt.prompt ?? ''}
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '16px',
                    lineHeight: '1.5',
                    borderRadius: 2
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '16px',
                  }
                }}
              />

              {/* Tool Type Selection - Moved to top for better visibility */}
              <FormControl fullWidth>
                <InputLabel>Tool Type</InputLabel>
                <Select
                  label="Tool Type"
                  name="toolType"
                  value={String(prompt.toolType || '')}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  {toolType.map(({ value, name }, idx) => (
                    <MenuItem key={idx} value={String(value)}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {[
                { name: 'rowNumber', label: 'Row Number', type: 'number', onlyForPredict: true },
                { name: 'name', label: 'Name', type: 'text', showAlways: true },
                { name: 'guidanceScale', label: 'Guidance Scale', type: 'number', onlyForPredict: true },
                { name: 'loraScale', label: 'Lora Scale', type: 'number', onlyForPredict: true },
                { name: 'numInferenceSteps', label: 'Number Inference Steps', type: 'number', onlyForPredict: true },
                { name: 'outputQuality', label: 'Output Quality', type: 'number', onlyForPredict: true },
                { name: 'numOfOutPuts', label: 'Number Of Outputs', type: 'number', onlyForPredict: true },
                { name: 'intputImageCount', label: 'Input Image Count',  showAlways: true },
                { name: 'cost', label: 'Cost (Credits)', type: 'number', showAlways: true },
                { name: 'templateId', label: 'Template ID', type: 'text', showAlways: true },
              ].filter(({ onlyForPredict, showAlways }) => {
                if (showAlways) return true; // Always show fields marked as showAlways
                if (onlyForPredict) return needsPredictionParams(prompt.toolType); // Only show for PREDICT_WITH_MODEL
                return true; // Show other fields by default
              }).map(({ name, label, type }) => (
                <TextField
                  key={name}
                  fullWidth
                  name={name}
                  variant="outlined"
                  label={label}
                  type={type}
                  value={prompt[name] ?? ''}
                  onChange={handleChange}
                  inputProps={{ ...(type === 'number' && { min: 0 }) }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              ))}
            </Box>
          </Box>
        </Paper>
        
        {/* Settings & Configuration Card */}
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          <Typography variant='h5' sx={{ mb: 4, fontWeight: 'bold', color: '#2c3e50' }}>
            Settings & Configuration
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Left column fields */}
              {[
                { label: 'Gender', key: 'gender', options: gender, showAlways: true },
                { label: 'Aspect Ratio', key: 'aspectRatio', options: aspectRatio, onlyForPredict: true },
                { label: 'Output Format', key: 'outputFormat', options: outPutFormat, onlyForPredict: true },
              ].filter(({ onlyForPredict, showAlways }) => {
                if (showAlways) return true; // Always show fields marked as showAlways
                if (onlyForPredict) return needsPredictionParams(prompt.toolType); // Only show for PREDICT_WITH_MODEL
                return true; // Show other fields by default
              }).map(({ label, key, options }) => (
                <FormControl key={key} fullWidth>
                  <InputLabel>{label}</InputLabel>
                  <Select
                    label={label}
                    name={key}
                    value={String(prompt[key] || '')}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    {options.map(({ value, name }, idx) => (
                      <MenuItem key={idx} value={String(value)}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Right column fields */}
              {[
                { label: 'Design Type', key: 'designType', options: designType, showAlways: true },
                { label: 'Inference Model', key: 'inferanceModelEnum', options: inferanceModelEnum, needsInference: true },
              ].filter(({ onlyForPredict, showAlways, needsInference }) => {
                if (showAlways) return true; // Always show fields marked as showAlways
                if (onlyForPredict) return needsPredictionParams(prompt.toolType); // Only show for PREDICT_WITH_MODEL
                if (needsInference) return needsInferenceModel(prompt.toolType); // Show for PREDICT_WITH_MODEL and IMG2IMG
                return true; // Show other fields by default
              }).map(({ label, key, options }) => (
                <FormControl key={key} fullWidth>
                  <InputLabel>{label}</InputLabel>
                  <Select
                    label={label}
                    name={key}
                    value={String(prompt[key] || '')}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    {options.map(({ value, name }, idx) => (
                      <MenuItem key={idx} value={String(value)}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
              {/* Tags Selection */}
              <Autocomplete
                multiple
                options={tags}
                getOptionLabel={(option) => option.name}
                value={selectedTags}
                onChange={(event, newValue) => {
                  setSelectedTags(newValue)
                  setPrompt(prev => ({
                    ...prev,
                    tagIds: newValue.map(tag => tag.id)
                  }))
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Tags"
                    placeholder="Select tags..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        '&:hover': { backgroundColor: '#bbdefb' }
                      }}
                    />
                  ))
                }
              />
            </Box>
          </Box>
        </Paper>

        {/* Tool Ratios Management Card */}
        <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <ToolRatioManager
            createMode={true}
            toolRatios={localRatios}
            onLocalRatiosChange={setLocalRatios}
          />
        </Paper>

        {/* Media Assets Card */}
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          <Typography variant='h5' sx={{ mb: 4, fontWeight: 'bold', color: '#2c3e50' }}>
            Media Assets
          </Typography>

          {/* ── Manuel görseller ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <ImageUploadField
              label="Before Image"
              description="Efekt uygulanmadan önceki örnek görsel · PNG / JPG / WebP"
              inputName="beforeimage"
              imageUrl={''}
              file={beforeImageFile}
              onImageClick={handleImageClick}
              onImageChange={handleBeforeImageSelected}
              onDelete={() => {
                setBeforeImageFile(null)
                setPrompt(prev => ({ ...prev, beforeImage: '' }))
              }}
            />
            <ImageUploadField
              label="Thumbnail Photo"
              description="Liste / kart görünümünde gösterilen ana kapak görseli · PNG / JPG / WebP"
              file={file}
              imageUrl={''}
              inputName="thumbnailphoto"
              onImageClick={handleImageClick}
              onImageChange={handleBannerFileSelected}
              onDelete={() => {
                setFile(null)
                setPrompt(prev => ({ ...prev, banner: '' }))
              }}
            />
            <ImageUploadField
              label="Prompt SVG"
              description="Prompt'u temsil eden ikon ya da grafik · SVG / PNG"
              inputName="promptsvg"
              imageUrl={''}
              file={promptSvgFile}
              onImageClick={handleImageClick}
              onImageChange={handlePromptSvgSelected}
              onDelete={() => {
                setPromptSvgFile(null)
                setPrompt(prev => ({ ...prev, promptSvg: '' }))
              }}
            />
            <ImageUploadField
              label="Second Banner"
              description="İkinci tanıtım / pazarlama görseli · PNG / JPG / WebP"
              file={secondBannerFile}
              imageUrl={''}
              inputName="secondBanner"
              onImageClick={handleImageClick}
              onImageChange={handleSecondBannerSelected}
              onDelete={() => {
                setSecondBannerFile(null)
                setPrompt(prev => ({ ...prev, secondBanner: '' }))
              }}
            />
            <ImageUploadField
              label="Detail Image"
              description="Prompt detay sayfasında gösterilen büyük görsel · PNG / JPG / WebP"
              file={detailImageFile}
              imageUrl={''}
              inputName="detailImage"
              onImageClick={handleImageClick}
              onImageChange={handleDetailImageSelected}
              onDelete={() => {
                setDetailImageFile(null)
                setPrompt(prev => ({ ...prev, detailImage: '' }))
              }}
            />
          </Box>

          {/* ── Video alanları ── */}
          <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid #e9ecef' }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 3, color: '#2c3e50' }}>
              Video Alanları
            </Typography>

            {/* Video + Auto Convert yan yana */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
              <ImageUploadField
                label="Video"
                description="Ana önizleme videosu — buraya MP4 yükle, diğerleri Auto Convert ile üretilir"
                file={videoFile}
                imageUrl={''}
                inputName="video"
                isVideo
                accept="video/mp4"
                onImageClick={handleImageClick}
                onImageChange={handleVideoSelected}
                onDelete={() => {
                  setVideoFile(null)
                  setPrompt(prev => ({ ...prev, videoUrl: '' }))
                }}
              />
              <VideoAutoConvert
                videoFile={videoFile}
                onConvertComplete={(results) => {
                  if (results.webm) setWebmFile(results.webm);
                  if (results.webp) setWebpFile(results.webp);
                  if (results.gif) setGifFile(results.gif);
                }}
              />
            </Box>

            {/* Auto-convert çıktıları: 3 kolon */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
              <ImageUploadField
                label="WEBM Video"
                description="Orijinal çözünürlük & FPS · format değişikliği"
                file={webmFile}
                imageUrl={''}
                inputName="webm"
                isVideo
                accept="video/webm"
                autoConvertable
                onImageClick={handleImageClick}
                onImageChange={handleWebmSelected}
                onDelete={() => {
                  setWebmFile(null)
                  setPrompt(prev => ({ ...prev, webmUrl: '' }))
                }}
              />
              <ImageUploadField
                label="WEBP Image"
                description="Animasyonlu · 360p, source fps (max 30)"
                file={webpFile}
                imageUrl={''}
                inputName="webp"
                accept="image/webp"
                autoConvertable
                onImageClick={handleImageClick}
                onImageChange={handleWebpSelected}
                onDelete={() => {
                  setWebpFile(null)
                  setPrompt(prev => ({ ...prev, webpUrl: '' }))
                }}
              />
              <ImageUploadField
                label="GIF"
                description="Animasyonlu · 360p, source fps (max 30)"
                inputName="gif"
                imageUrl={''}
                file={gifFile}
                accept="image/gif"
                autoConvertable
                onImageClick={handleImageClick}
                onImageChange={handleGifSelected}
                onDelete={() => {
                  setGifFile(null)
                  setPrompt(prev => ({ ...prev, gifUrl: '' }))
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Box>

      {openImageModal && (
        <Box
          onClick={() => setOpenImageModal(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.7)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            src={modalImageSrc}
            alt="Full Preview"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: '10px',
            }}
            onClick={e => e.stopPropagation()}
          />
        </Box>
      )}
    </Box>
  )
}
